/**
 * The StarRocks engine profile behind the load-stats badge's hover text. Fetched after the report
 * paints because StarRocks publishes a profile a second or two after the query ends; `loadQueryStats`
 * owns the retry and resolves to null, since a missing profile must never fail a report.
 */

import { useEffect, useState } from 'react';
import type { PnlQueryStats } from 'src/components/star-grid/queryStats.types';
import { loadQueryStats } from 'src/components/star-grid/services/queryStatsService';
import { afterReportPaint } from 'src/components/star-grid/utils/afterReportPaint';

export const useReportQueryStats = (
  reportId: string | undefined,
  queryId: string | null | undefined,
  loading: boolean,
  factTable?: string | null
): PnlQueryStats | undefined => {
  const [stats, setStats] = useState<{ readonly queryId: string; readonly stats: PnlQueryStats }>();

  useEffect(() => {
    const numericId = Number(reportId);
    if (loading || !queryId || !Number.isSafeInteger(numericId) || numericId <= 0) return undefined;
    const controller = new AbortController();
    const receive = (found: PnlQueryStats | null): void => {
      if (found && !controller.signal.aborted) setStats({ queryId, stats: found });
    };
    const cancelPaint = afterReportPaint(() => {
      void loadQueryStats(queryId, numericId, controller.signal, receive, factTable ?? undefined).then(receive);
    });
    return () => {
      cancelPaint();
      controller.abort();
    };
  }, [reportId, queryId, loading, factTable]);

  // Keyed by query id so a re-query never shows the previous query's counters next to new numbers.
  return !loading && stats && stats.queryId === queryId ? stats.stats : undefined;
};
